import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  setDoc,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Services/firebase-config';

const ValueChainEditorDetails = () => {
  const { valueChainId } = useParams();
  const location = useLocation();
  const valueChain = location.state?.valueChain || {};

  const [user, setUser] = useState(null);
  const [details, setDetails] = useState({
    basevalue: '',
    justification: '',
    cost: '',
  });
  const [submittedChains, setSubmittedChains] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchChains = async () => {
      if (!user || !valueChainId) return;
      try {
        const detailsRef = collection(db,`users/${user.uid}/valueChains/${valueChainId}/Details` );
        const snapshot = await getDocs(detailsRef);
        const chains = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubmittedChains(chains);
      } catch (error) {
        console.error('Error fetching chains:', error);
      }
    };

    fetchChains();
  }, [user, valueChainId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveToProductCollection = async (dataToSave, docId = null) => {
    if (!valueChain.name) return;
    const productDocRef = doc( db,`products/${valueChain.name}/valueChainDetails`, docId || crypto.randomUUID() );
    await setDoc(productDocRef, dataToSave, { merge: true });
  };

  const handleSubmit = async () => {
    if (!details.basevalue || !details.justification || !details.cost) {
      alert('Please fill in all fields.');
      return;
    }

    if (!user || !valueChainId) return;

    try {
      const parsedCost = parseFloat(details.cost);
      if (isNaN(parsedCost)) {
        alert('Cost must be a number.');
        return;
      }

      const dataToSave = {
        basevalue: details.basevalue,
        justification: details.justification,
        cost: parsedCost,
      };

      const detailsRef = collection( db,`users/${user.uid}/valueChains/${valueChainId}/Details` );

      if (editingId !== null) {
        const chainToEdit = submittedChains.find(c => c.id === editingId);
        const docRef = doc(db, `users/${user.uid}/valueChains/${valueChainId}/Details`,chainToEdit.id);
        await updateDoc(docRef, dataToSave);
        await saveToProductCollection(dataToSave, chainToEdit.id);

        const updated = submittedChains.map(c => c.id === editingId ? { ...dataToSave, id: editingId } : c);
 setSubmittedChains(updated);
      } else {
        const docRef = await addDoc(detailsRef, dataToSave);
        setSubmittedChains((prev) => [...prev, { ...dataToSave, id: docRef.id }]);
        await saveToProductCollection(dataToSave, docRef.id);
      }

      setDetails({ basevalue: '', justification: '', cost: '' });
      setEditingId(null);
      alert('Submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Submission failed. Try again.');
    }
  };

  const handleDelete = async (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
      if (itemToDelete === null || !user || !valueChainId) return;
      try {
          const chain = submittedChains.find(c => c.id === itemToDelete);
          const docRef = doc(db, `users/${user.uid}/valueChains/${valueChainId}/Details`, chain.id);
          await deleteDoc(docRef);

          // Delete from product collection too
          if (valueChain.name) {
              const productDocRef = doc(db, `products/${valueChain.name}/valueChainDetails`, chain.id);
              await deleteDoc(productDocRef);
          }

          setSubmittedChains((prev) => prev.filter((c) => c.id !== itemToDelete));
          alert('Deleted successfully!');
      } catch (error) {
          console.error('Error deleting entry:', error);
          alert('Delete failed. Try again.');
      }

      setShowDeleteModal(false);
      setItemToDelete(null);
  };


  const handleEdit = (chain) => {
    setDetails({ ...chain, cost: chain.cost.toString() });
    setEditingId(chain.id);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-lg mx-auto bg-white shadow-md rounded-lg p-6">
        <h1 className="text-2xl font-bold mb-4 text-gray-800">Value Chain Editor</h1>

        <div className="mb-6">
          <label htmlFor="basevalue" className="block text-sm font-medium text-gray-700">
            Base Value Chain Input
          </label>
          <textarea
            id="basevalue"
            name="basevalue"
            value={details.basevalue}
            onChange={handleChange}
            rows="4"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Enter the base value chain input"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="justification" className="block text-sm font-medium text-gray-700">
            Justification for BVI
          </label>
          <textarea
            id="justification"
            name="justification"
            value={details.justification}
            onChange={handleChange}
            rows="4"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Why do you think this input is right?"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="cost" className="block text-sm font-medium text-gray-700">
            Cost of Input
          </label>
          <input
            id="cost"
            name="cost"
            type="number"
            value={details.cost}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Enter the cost"
          />
        </div>

        <div className="flex justify-between">
          <button
            onClick={handleSubmit}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          >
            {editingId !== null ? 'Update' : 'Submit'}
          </button>
        </div>
      </div>

      <div className="mt-8 max-w-4xl mx-auto bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Value Chain Details</h2>
        <table className="min-w-full table-auto border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-300 px-4 py-2">Base Value</th>
              <th className="border border-gray-300 px-4 py-2">Justification</th>
              <th className="border border-gray-300 px-4 py-2">Cost</th>
              <th className="border border-gray-300 px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {submittedChains.map((chain, index) => (
              <tr key={chain.id}>
                <td className="border border-gray-300 px-4 py-2 break-words">{chain.basevalue}</td>
                <td className="border border-gray-300 px-4 py-2 break-words">{chain.justification}</td>
                <td className="border border-gray-300 px-4 py-2 break-words">{chain.cost}</td>
                <td className="border border-gray-300 px-4 py-2 flex gap-2">
                  <button
                    onClick={() => handleEdit(chain)}
                    className="bg-yellow-500 text-white px-3 py-1 rounded-md hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(chain.id)}
                    className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-1/3">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p>Are you sure you want to delete this item?</p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="ml-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md shadow hover:bg-gray-400"
              >
                Cancel
              </button>
              <button onClick={confirmDelete} className="bg-red-500 text-white px-4 py-2 rounded-md shadow hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValueChainEditorDetails;
